<script setup lang="ts">
import {
  onBeforeMount, onMounted, ref, computed, nextTick,
} from 'vue';
import {
  Storage, Fusion, GTrack, I18N, showFleetHomePageTab,
} from 'core';
import {
  getUidByTicket,
  getCookie,
  getTimeOrUnit,
  getSwitchSet,
} from 'apis';
import ModalSelect from 'components/ModalSelect/index.vue';
import VConsole from 'vconsole';
import {
  useCommonStore, useCacheStore, useUserStore, useTrackStore, useBillSwitchStore, useGenerousSwitchStore, useTaxStore,
} from 'store';
import { useRoute, useRouter } from 'vue-router';
import { countryThemeList } from 'constant';

const commonStore = useCommonStore();
const cacheStore = useCacheStore();
const userStore = useUserStore();
const trackStore = useTrackStore();
const billSwitchStore = useBillSwitchStore();
const generousSwitchStore = useGenerousSwitchStore();
const taxStore = useTaxStore();

const { $state } = cacheStore;
const route = useRoute();
const router = useRouter();
const pageRef = ref();

const cachedViews = computed(() => $state.cachedViews.map((item: any) => item.name));
router.beforeEach((to, from) => {
  if (from.name) {
    if (from.meta.keepAlive) {
      cacheStore.addCachedView({
        name: (from.name as string),
        scrollY: pageRef.value?.$el?.scrollTop,
      });
    }
  } else if (to.name && to.meta.keepAlive) {
    cacheStore.addCachedView({
      name: (to.name as string),
      scrollY: pageRef.value?.$el?.scrollTop,
    });
  }
});

router.afterEach((to) => {
  nextTick(() => {
    const cacheView = $state.cachedViews.find((v: any) => v.name === to.name);
    if (cacheView && pageRef.value && pageRef.value.$el) {
      pageRef.value.$el.scrollTop = cacheView.scrollY;
    }
  });
});

const langList = [
  {
    value: 'zh-CN',
    text: 'zh-CN',
  },
  {
    value: 'pt-BR',
    text: 'pt-BR',
  },
  {
    value: 'es-MX',
    text: 'es-MX',
  },
  {
    value: 'es-CO',
    text: 'es-CO',
  },
  {
    value: 'es-CR',
    text: 'es-CR',
  },
  {
    value: 'es-CL',
    text: 'es-CL',
  },
  {
    value: 'es-419',
    text: 'es-419',
  },
  {
    value: 'en-US',
    text: 'en-US',
  },
  {
    value: 'ru-RU',
    text: 'ru-RU',
  },
];

const langModal = ref<any>();

const isTest = ref(false);

// 切换主题，默认是墨西哥主题
const changeTheme = (country: string) => {
  const lowerCountry = (country || 'MX')?.toLowerCase() || 'mx';
  document.documentElement.className = countryThemeList.includes(lowerCountry) ? `theme_${lowerCountry}` : 'theme_mx';
};

onBeforeMount(() => {
  // 接受邀请页不要验证ticket
  if (!(window.location.href.includes('/driver/driverJoinFleet') || window.location.href.includes('headpic'))) {
    // 资金单位
    getTimeOrUnit().then((res: any) => {
      commonStore.setCurrentUnit(res?.currency_unit || 'R$');
    });

    getSwitchSet().then((res: any) => {
      const functionSet: any = {};
      (res || []).forEach((item: any) => {
        functionSet[item.feature_name] = item.switch;
      });
      // 资金流放量开关
      billSwitchStore.setBillSwitch(functionSet?.new_cash_flow);
      // 是否展示税收开关
      taxStore.setTaxSwitch(functionSet?.tax_split);
      // 主页新界面开关
      generousSwitchStore.setGenerousSwitch(functionSet?.finance_optimize);
    });
  }
});

onMounted(() => {
  Storage.initQuery();

  // 切换主题
  const country: string = Storage.getQuery('location_country');
  changeTheme(country);

  // 接受邀请页不要验证ticket
  if (!(window.location.href.includes('/driver/driverJoinFleet') || window.location.href.includes('headpic'))) {
    // 验证ticket
    getUidByTicket().then((res: any) => {
      if (res?.is_test) {
        localStorage.setItem('vConsole_switch_x', '0');
        localStorage.setItem('vConsole_switch_y', '200');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const vConsole = new VConsole();
        isTest.value = true;
      }
      sessionStorage.fleet_uid = res?.uid;
      sessionStorage.driver_status = res?.driver_status;
      (window as any).gTrack.setUserId({ id: res?.uid });
    });
    getCookie().then((res: any) => {
      userStore.setUserinfo(res);
      if (!res?.history_bind_car) {
        if (Fusion?.showNativeTips) {
          Fusion.showNativeTips({
            tabTips: [ // 数组,支持多个Tab Tips排队展示（待开发）
              {
                tabIndex: '3', // 对相应tab做引导
                tabTipsTag: 'fleetapp_vehiclepage_guidebubble', // tip的tag，作为每条tip的唯一标识
                tipsString: I18N.translate('Fleet_optimizaton__ZYCj', '前往绑定车辆'), // 文案
                limitedShowTimes: 3, // 展示限制次数
                autoDismissTime: 10000, // 10000 ms后自动消失 ； 不传不自动消失。
              },
            ],
            tabDots: {}, // 显示tab小红点
          });
        }
      }
    });
  }
});

const ticketChange = (e: any) => {
  const { value } = e.target;
  const url = Storage.setUrlLenCodeQuery({
    ticket: value.replace(/"/g, ''),
  });
  window.location.replace(url);
  window.location.reload();
};

const langSwitch = () => {
  langModal.value?.open(Storage.getLang());
};

const langConfirm = (value: any) => {
  sessionStorage.lang = value[0].value;
  window.location.reload();
};

window.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    // 页面可见时，触发埋点
    (trackStore?.trackList || []).forEach((track: any) => GTrack.sw(track));
    // 隐藏页面
    const curRouterName = router.currentRoute.value.name || '';
    showFleetHomePageTab(curRouterName as string);
  }
});
</script>

<template>
  <router-view
    v-if="!commonStore.isUnauth || route.name === 'driverJoinFleet' || route.name === 'profileHeadPic' || route.name === 'uploadHeadPic' || route.name === 'resPage'"
    v-slot="{ Component }"
    ref="pageRef"
  >
    <transition>
      <keep-alive :include="cachedViews">
        <component :is="Component" />
      </keep-alive>
    </transition>
  </router-view>
  <div
    v-else-if="route.name !== 'driverJoinFleet' && route.name !== 'profileHeadPic' && route.name !== 'uploadHeadPic' && route.name !== 'resPage'"
    class="top_page"
    :class="$style.unauth"
  >
    <van-field
      placeholder="验证失败，请粘贴有效Ticket"
      @change="ticketChange"
    />
  </div>
  <template v-if="isTest">
    <div
      :class="$style.switch_lang"
      @click="langSwitch"
    >
      {{ Storage.getLang() }}
    </div>
    <ModalSelect
      ref="langModal"
      title="请选择语言"
      :columns="langList"
      @confirm="langConfirm"
    />
  </template>
</template>

<style lang="less" module>
.unauth {
  display: flex;
  align-items: center;
  justify-content: center;
}

.switch_lang {
  position: fixed;
  right: 0;
  bottom: 300px;
  color: #ffffff;
  background-color: #FF7226;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 100;
}
</style>
